import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { GravityProvider } from '@crosscode/gravity-components'
import { getKitTheme, getKitTokens } from '@/app/theme'

export type KitTheme = 'light' | 'dark'
export type KitDesign = 'studio' | 'liquid'
export type KitFont = 'golos' | 'onest' | 'inter' | 'manrope' | 'syne' | 'exo2'
export type KitScale = 'sm' | 'md' | 'lg'

const STORAGE_KEY = 'kit-design-prefs'
const SIDEBAR_KEY = 'kit-sidebar-collapsed'

export interface DesignPrefs {
  theme: KitTheme
  design: KitDesign
  font: KitFont
  scale: KitScale
}

const DEFAULTS: DesignPrefs = {
  theme: 'light',
  design: 'studio',
  font: 'golos',
  scale: 'md',
}

function readPrefs(): DesignPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULTS
    const parsed = { ...DEFAULTS, ...JSON.parse(raw) } as DesignPrefs
    if (parsed.font === 'syne' || parsed.font === 'exo2') parsed.font = 'golos'
    return parsed
  } catch {
    return DEFAULTS
  }
}

export function applyDesignAttrs(prefs: Partial<DesignPrefs> = {}) {
  const next = { ...readPrefs(), ...prefs }
  const root = document.documentElement
  root.setAttribute('data-theme', next.theme)
  root.setAttribute('data-design', next.design)
  root.setAttribute('data-font', next.font)
  root.setAttribute('data-scale', next.scale)
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  return next
}

export function readSidebarCollapsed(): boolean {
  try {
    const raw = localStorage.getItem(SIDEBAR_KEY)
    if (raw === '1') return true
    if (raw === '0') return false
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches) return true
    return false
  } catch {
    return false
  }
}

export function writeSidebarCollapsed(collapsed: boolean) {
  try {
    localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0')
  } catch {
    /* ignore */
  }
}

interface DesignContextValue {
  prefs: DesignPrefs
  setPrefs: (partial: Partial<DesignPrefs>) => void
  toggleTheme: () => void
  sidebarCollapsed: boolean
  setSidebarCollapsed: (v: boolean) => void
}

const DesignContext = createContext<DesignContextValue | null>(null)

export function useDesign() {
  const ctx = useContext(DesignContext)
  if (!ctx) throw new Error('useDesign must be used within DesignProvider')
  return ctx
}

/** Applies Dispatch prefs on <html> and syncs Ant/Gravity theme */
export function DesignProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefsState] = useState<DesignPrefs>(() => {
    if (typeof document !== 'undefined') {
      return applyDesignAttrs()
    }
    return DEFAULTS
  })
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(() => readSidebarCollapsed())

  const setPrefs = useCallback((partial: Partial<DesignPrefs>) => {
    setPrefsState(applyDesignAttrs(partial))
  }, [])

  const toggleTheme = useCallback(() => {
    setPrefsState((prev) => applyDesignAttrs({ theme: prev.theme === 'dark' ? 'light' : 'dark' }))
  }, [])

  const setSidebarCollapsed = useCallback((v: boolean) => {
    writeSidebarCollapsed(v)
    setSidebarCollapsedState(v)
  }, [])

  useEffect(() => {
    applyDesignAttrs(prefs)
  }, [prefs])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'l') {
        e.preventDefault()
        toggleTheme()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggleTheme])

  const value = useMemo(
    () => ({ prefs, setPrefs, toggleTheme, sidebarCollapsed, setSidebarCollapsed }),
    [prefs, setPrefs, toggleTheme, sidebarCollapsed, setSidebarCollapsed],
  )

  const theme = getKitTheme(prefs.theme)
  const tokens = getKitTokens(prefs.theme)

  return (
    <DesignContext.Provider value={value}>
      <GravityProvider theme={theme} tokens={tokens} key={prefs.theme}>
        {children}
      </GravityProvider>
    </DesignContext.Provider>
  )
}
